// Search index mapping all navigable pages and sections
// Each item has: id, title, description, url, keywords, featureId (optional)

export interface SearchItem {
  id: string
  title: string
  description: string
  url: string // relative to /{orgId}
  keywords: string[] // additional search terms
  featureId?: string // if requires a specific feature to be enabled
  section?: string // parent section name
  icon?: string // icon name from lucide
}

export const searchIndex: SearchItem[] = [
  // Dashboard
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Overview and analytics for your organization',
    url: '/assist',
    keywords: ['home', 'overview', 'main', 'start'],
    icon: 'LayoutDashboard'
  },
  
  // Recommendation Engine
  {
    id: 'recommendations',
    title: 'Recommendation Engine',
    description: 'Smart product and content recommendations',
    url: '/assist/recommendations',
    keywords: ['ai', 'ml', 'suggest', 'personalization'],
    featureId: 'recommendation-engine',
    icon: 'Sparkles'
  },
  {
    id: 'recommendations-users',
    title: 'Recommendation Users',
    description: 'Manage users for recommendations',
    url: '/assist/recommendations/users',
    keywords: ['customers', 'profiles', 'audience'],
    featureId: 'recommendation-engine',
    section: 'Recommendation Engine',
    icon: 'Users'
  },
  {
    id: 'recommendations-recommenders',
    title: 'Recommenders',
    description: 'Configure recommendation algorithms',
    url: '/assist/recommendations/recommenders',
    keywords: ['algorithms', 'models', 'config'],
    featureId: 'recommendation-engine',
    section: 'Recommendation Engine',
    icon: 'Sparkles'
  },
  {
    id: 'recommendations-products',
    title: 'Products',
    description: 'Manage product catalog for recommendations',
    url: '/assist/recommendations/products',
    keywords: ['items', 'catalog', 'inventory'],
    featureId: 'recommendation-engine',
    section: 'Recommendation Engine',
    icon: 'Package'
  },
  
  // Signals
  {
    id: 'signals',
    title: 'Signals',
    description: 'Event tracking and data collection',
    url: '/assist/signals',
    keywords: ['events', 'tracking', 'analytics', 'data'],
    icon: 'Activity'
  },
  {
    id: 'signals-models',
    title: 'Signal Models',
    description: 'Define and manage signal data models',
    url: '/assist/signals/models',
    keywords: ['schema', 'structure', 'types', 'events'],
    section: 'Signals',
    icon: 'Box'
  },
  {
    id: 'signals-code',
    title: 'Signal Code',
    description: 'Get code snippets for signal integration',
    url: '/assist/signals/code',
    keywords: ['sdk', 'integration', 'snippet', 'javascript', 'embed'],
    section: 'Signals',
    icon: 'Code2'
  },
  
  // Analytics
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'Performance insights and reports',
    url: '/assist/analytics',
    keywords: ['reports', 'metrics', 'performance', 'charts', 'graphs'],
    icon: 'BarChart3'
  },

  // Docs
  {
    id: 'docs',
    title: 'Documentation',
    description: 'Knowledge base and guides for your organization',
    url: '/assist/docs',
    keywords: ['docs', 'help', 'guide', 'knowledge', 'articles', 'tutorials'],
    icon: 'BookOpen'
  },
  
  // Settings
  {
    id: 'settings',
    title: 'Settings',
    description: 'Platform configuration and preferences',
    url: '/assist/settings',
    keywords: ['config', 'preferences', 'options'],
    icon: 'Settings'
  },
  {
    id: 'settings-general',
    title: 'General Settings',
    description: 'Organization profile and appearance',
    url: '/assist/settings?tab=general',
    keywords: ['profile', 'name', 'logo', 'theme', 'dark mode'],
    section: 'Settings',
    icon: 'Building'
  },
  {
    id: 'settings-members',
    title: 'Team Members',
    description: 'Invite and manage team members',
    url: '/assist/settings?tab=members',
    keywords: ['team', 'invite', 'users', 'permissions', 'roles'],
    section: 'Settings',
    icon: 'Users'
  },
  {
    id: 'settings-api-keys',
    title: 'API Keys',
    description: 'Manage API keys for integration',
    url: '/assist/settings?tab=api-keys',
    keywords: ['tokens', 'authentication', 'access', 'integration'],
    section: 'Settings',
    icon: 'Key'
  },
  {
    id: 'settings-admin-keys',
    title: 'Admin Keys',
    description: 'Manage admin keys for programmatic API access',
    url: '/assist/settings?tab=admin-keys',
    keywords: ['admin', 'programmatic', 'api', 'management', 'models', 'docs'],
    section: 'Settings',
    icon: 'Key'
  },
  {
    id: 'settings-notifications',
    title: 'Notifications',
    description: 'Configure notification preferences',
    url: '/assist/settings?tab=notifications',
    keywords: ['alerts', 'email', 'reports'],
    section: 'Settings',
    icon: 'Bell'
  },
  {
    id: 'settings-security',
    title: 'Security',
    description: 'Security settings and audit logs',
    url: '/assist/settings?tab=security',
    keywords: ['audit', 'logs', 'access', 'protection'],
    section: 'Settings',
    icon: 'Shield'
  },
  
  // Help
  {
    id: 'help',
    title: 'Help & Support',
    description: 'Documentation and support resources',
    url: '/assist/help',
    keywords: ['docs', 'documentation', 'support', 'faq', 'guide'],
    icon: 'HelpCircle'
  },
  
  // Profile
  {
    id: 'profile',
    title: 'Profile',
    description: 'Your personal profile settings',
    url: '/assist/profile',
    keywords: ['account', 'me', 'personal'],
    icon: 'User'
  }
]

// Search function with fuzzy matching
export function searchItems(
  query: string,
  enabledFeatures: string[] = []
): SearchItem[] {
  if (!query.trim()) return []
  
  const searchTerms = query.toLowerCase().split(/\s+/)
  
  return searchIndex
    // Filter by feature access
    .filter(item => !item.featureId || enabledFeatures.includes(item.featureId))
    // Score and filter by search relevance
    .map(item => {
      const titleLower = item.title.toLowerCase()
      const descLower = item.description.toLowerCase()
      const keywordsLower = item.keywords.map(k => k.toLowerCase())
      const sectionLower = (item.section || '').toLowerCase()
      
      let score = 0
      
      for (const term of searchTerms) {
        // Exact title match (highest priority)
        if (titleLower === term) score += 100
        // Title starts with term
        else if (titleLower.startsWith(term)) score += 50
        // Title contains term
        else if (titleLower.includes(term)) score += 30
        // Keyword exact match
        else if (keywordsLower.includes(term)) score += 25
        // Keyword partial match
        else if (keywordsLower.some(k => k.includes(term))) score += 15
        // Description contains term
        else if (descLower.includes(term)) score += 10
        // Section contains term
        else if (sectionLower.includes(term)) score += 5
      }
      
      return { item, score }
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item)
    .slice(0, 10) // Limit results
}
